import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GroupFragment, UserMadFullFragment } from "../../graphql/fragments";
import useGetGroups from "../../graphql/hooks/queries/useGetGroups";
import { GET_USER } from "../../graphql/hooks/queries/useGetUserFull";
import { Group } from "../../graphql/types";
import useAddNotification from "../../hooks/useAddNotification";
import useInsertUser from "../../hooks/useInsertUser";
import useViewer from "../../hooks/useViewer";
import useTranslations from "../../hooks/useTranslations";
import { InsertGroupDialog } from "../groups/InsertGroupDialog";
import { CheckIcon, CloseIcon, EditIcon, SettingsIcon } from "../icons";
import SearchPopover from "../utils/SearchPopover";
import { Badge, Button, H4 } from "../wombat-ui";
import GroupBadge from "../groups/GroupBadge";

const GroupSearchItem: React.FC<{
  group: GroupFragment;
  emphasized: boolean;
  baseId?: string;
}> = ({ group, emphasized, baseId }) => (
  <div
    className="flex items-center justify-between"
    id={`${baseId}${group.id}`}
  >
    <div className="flex space-x-2">
      {emphasized && <CheckIcon iconClassName="w-5 h-5 text-primary-500" />}
      <p className="text-sm font-medium text-dark-100">{group.name}</p>
    </div>
    {emphasized && (
      <div>
        <CloseIcon iconClassName="w-3 h-3 text-dark-100" />
        <span className="sr-only">Remove group</span>
      </div>
    )}
  </div>
);

interface UserGroupsProps {
  user: UserMadFullFragment;
}

export const UserGroups: React.FC<UserGroupsProps> = ({ user }) => {
  const t = useTranslations();

  const {
    data: groupsData,
    loading: groupsLoading,
    refetch: refetchGroups,
  } = useGetGroups();
  const groups = groupsData?.groups?.collection || [];
  const [addNotification] = useAddNotification();
  const [newGroupDialog, setNewGroupDialog] = useState(undefined);

  const [updateUserGroups] = useInsertUser({
    refetchQueries: [{ query: GET_USER, variables: { id: user.id } }],
  });

  const handleItemSelected = useCallback(
    (group: GroupFragment, emphasized: boolean) => {
      let newGroupsIds: string[];

      if (!emphasized)
        newGroupsIds = [...user.groups, group].map(({ id }) => id);
      else
        newGroupsIds = [...user.groups]
          .filter(({ id }) => id !== group.id)
          .map(({ id }) => id);

      updateUserGroups({
        variables: {
          input: {
            id: user.id,
            groupIds: newGroupsIds,
          },
        },
      }).then(() =>
        addNotification({
          variables: {
            title: t("userProfile.notification.success") as string,
            status: "success",
          },
        })
      );
    },
    [updateUserGroups, user]
  );

  const filteredGroups = useMemo(
    () =>
      groups.filter(
        ({ id: groupId }) =>
          !user.groups.find(({ id: userGroupId }) => groupId === userGroupId)
      ),
    [user, groups]
  );

  const { isCapableOfInsertingGroups } = useViewer();

  return (
    <form>
      <div className="flex items-center justify-between">
        <H4>{t("collaborator.groups")}</H4>
        {user.canInsert && (
          <SearchPopover
            title={t("collaborator.groups") as string}
            placeholder={t("collaborator.filterGroups") as string}
            loading={groupsLoading}
            items={filteredGroups}
            align="bottom-right"
            emphasized={[{ elements: user.groups }]}
            onItemSelected={handleItemSelected}
            onSearchChange={(value) => {
              refetchGroups({ q: value });
            }}
            renderItem={(item: GroupFragment, emphasized: boolean) => (
              <GroupSearchItem
                group={item}
                emphasized={emphasized}
                baseId={`${user.id}`}
              />
            )}
            button={
              <Button variant="ghost" icon={SettingsIcon}>
                <span className="sr-only">{t("groups.changeUserGroups")}</span>
              </Button>
            }
            noMatch={
              <div className="flex flex-col space-y-2">
                <p className="text-sm text-light-900">
                  {t("wombat.select.emptyResult")}
                </p>
                {isCapableOfInsertingGroups && (
                  <Button
                    variant="subtle"
                    id="addGroupButton"
                    onClick={() => setNewGroupDialog({})}
                  >
                    {t("collaborator.addGroup")}
                  </Button>
                )}
              </div>
            }
            footer={
              isCapableOfInsertingGroups && (
                <Link
                  to={"/groups/edit"}
                  className="flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-light-0"
                >
                  {t("collaborator.editGroups")}
                  <EditIcon iconClassName="w-4 h-4" />
                </Link>
              )
            }
          />
        )}
      </div>
      {user.groups.length === 0 && (
        <p className="text-sm text-light-900">{t("collaborator.noGroups")}</p>
      )}
      <div className="flex flex-wrap -mx-1.5 py-2">
        {user.groups.map((group: Group) => (
          <div className="px-1.5 py-1" key={group.id}>
            <GroupBadge group={group} />
          </div>
        ))}
      </div>
      <InsertGroupDialog
        group={newGroupDialog}
        onClose={() => setNewGroupDialog(undefined)}
      />
    </form>
  );
};
